import { Flame } from "lucide-react";

// Renders a rolling 7-day heat-strip (oldest → today). Each cell is one day;
// a filled dot means the learner was active that day. Designed to feel like
// a "consistency" glance, not a chore tracker.

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function lastNDays(n) {
    // Backend stores `active_dates` from UTC `date.today()`, so we must also
    // compute "today" in UTC — otherwise a learner east of UTC can see the
    // wrong day lit late in their evening.
    const out = [];
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    for (let i = n - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setUTCDate(today.getUTCDate() - i);
        out.push(d);
    }
    return out;
}

function toIsoDate(d) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export default function StreakCalendar({ activeDates = [], streak = 0 }) {
    const days = lastNDays(7);
    const activeSet = new Set(activeDates);
    const activeCount = days.filter((d) => activeSet.has(toIsoDate(d))).length;

    let subline = "Your last 7 days.";
    if (activeCount === 0) subline = "A single lesson counts — today is a good day to start.";
    else if (activeCount === 7) subline = "Seven for seven. Impressive week.";
    else if (activeCount >= 5) subline = `${activeCount} out of 7 days — this is what a habit looks like.`;
    else subline = `${activeCount} out of 7 days.`;

    return (
        <section className="card-rw p-6 md:p-7 mb-8" data-testid="streak-calendar">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FDE9DD] flex items-center justify-center">
                        <Flame className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                    </div>
                    <div>
                        <p className="label-eyebrow">This week</p>
                        <h3 className="text-xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                            {streak > 0 ? `${streak} ${streak === 1 ? "day" : "days"} in a row` : "Start your streak"}
                        </h3>
                    </div>
                </div>
                <p className="text-sm text-[#515B57] max-w-[260px] text-right" data-testid="streak-calendar-subline">
                    {subline}
                </p>
            </div>
            <div className="grid grid-cols-7 gap-2" role="list" aria-label="Weekly activity">
                {days.map((d) => {
                    const iso = toIsoDate(d);
                    const isActive = activeSet.has(iso);
                    const isToday = iso === toIsoDate(new Date());
                    const label = DAY_LABELS[d.getUTCDay()];
                    return (
                        <div
                            key={iso}
                            role="listitem"
                            data-testid={`streak-day-${iso}`}
                            data-active={isActive ? "true" : "false"}
                            data-today={isToday ? "true" : "false"}
                            className="flex flex-col items-center gap-2"
                            title={`${d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}${isActive ? " · active" : ""}`}
                        >
                            <div
                                className={
                                    "w-full aspect-square rounded-lg flex items-center justify-center transition-transform " +
                                    (isActive
                                        ? "bg-[#2D5D4E] text-[#FAF9F6] shadow-[0_1px_0_rgba(0,0,0,0.05)] hover:scale-[1.04]"
                                        : "bg-[#F5F1E4] text-[#B7BEB9] border border-[#E5E7E2]")
                                }
                            >
                                {isActive ? (
                                    <Flame className="w-4 h-4" strokeWidth={2} />
                                ) : (
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#D6D8D2]" />
                                )}
                            </div>
                            <span
                                className={
                                    "text-xs " +
                                    (isToday
                                        ? "text-[#2D5D4E] font-semibold"
                                        : "text-[#819888]")
                                }
                            >
                                {label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
